/**
 * usePushNotifications Hook - Manages Web Push notification subscription
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Check if push notifications are supported
const isPushSupported = () => {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
};

export function usePushNotifications() {
  const { api, isAuthenticated, token } = useAuth();
  const [isSupported] = useState(() => isPushSupported());
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState(() => 
    isPushSupported() ? Notification.permission : 'default'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  // Fetch VAPID public key
  useEffect(() => {
    const fetchVapidKey = async () => {
      try {
        const response = await api.get('/push/vapid-public-key');
        setVapidPublicKey(response.data.publicKey);
      } catch (err) {
        console.log('Push notifications not configured on server');
      }
    };

    if (isAuthenticated && isSupported) {
      fetchVapidKey();
    }
  }, [api, isAuthenticated, isSupported]);

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated || !isSupported) return;

      try {
        const response = await api.get('/push/status');
        setIsSubscribed(response.data.subscribed);
      } catch (err) {
        console.log('Error checking push status:', err);
      }
    };

    checkSubscription();
  }, [api, isAuthenticated, isSupported]);

  // Convert base64 to Uint8Array for applicationServerKey
  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !vapidPublicKey) {
      setError('Push notifications não suportadas');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        setError('Permissão para notificações negada');
        setLoading(false);
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const subscriptionJson = subscription.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys,
        expirationTime: subscriptionJson.expirationTime
      });

      setIsSubscribed(true);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('Error subscribing to push:', err);
      setError('Erro ao ativar notificações');
      setLoading(false);
      return false;
    }
  }, [api, isSupported, vapidPublicKey, urlBase64ToUint8Array]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;

    setLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Remove from backend
        await api.delete(`/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`);
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('Error unsubscribing from push:', err);
      setError('Erro ao desativar notificações');
      setLoading(false);
      return false;
    }
  }, [api, isSupported]);

  // Send test notification
  const sendTest = useCallback(async () => {
    if (!isSubscribed) return false;

    try {
      await api.post('/push/test', {
        title: 'Teste ViajeJunto 🚀',
        body: 'Esta é uma notificação de teste!'
      });
      return true;
    } catch (err) {
      console.error('Error sending test notification:', err);
      return false;
    }
  }, [api, isSubscribed]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTest
  };
}

export default usePushNotifications;
